import _ from 'lodash';
import { estimateDeskPhoneTax, estimateTax, getDefaultEcmInfo } from '@/apis/tax';
import { getEffectiveCurrency } from "@/utils/currency";
import {getDefaultCvvReAuthInfo, ttrTaxStatusList} from "@/store/modules/payment";
import { NEXT_CYCLE_KEY_WITH_BILLING_CYCLE_MAP, FetchStatus } from '@/common/constants';
import { isEmptyVal } from '@/utils';
import { isFromCancelCampaign } from '@/views/config/coupon/helper/utils';
import { compareDate, getNextPstCycleDate } from '@/utils/dateformat';
import { plansof } from '@/utils/chain';
import { getRealPlanCode } from '@/common/service/parse';
import { US_short } from '@/common/regionUtils';
import Enums from '@/common/enums/Enums';
import { logger } from '@/utils/log';
import { getPartnershipParams } from '@/views/config/partner-refer/helper/params';
import { stringifyPhoneNumber } from '@/views/checkout/address/helper/phone-number-helper';

const state = {
  taxes: [],
  isTaxEstimatedBefore: false,
  isTaxUpdateAttempted: false,
  isLoadingTax: false,
  isTaxEstimatedSuccess: false,
  certificateId: '',
  deskphoneTaxes: [],
  deskphoneTaxStatus: FetchStatus.IDLE
}

const getters = {
  deskphoneTaxStatus: state => state.deskphoneTaxStatus,
  getTaxes: (state) => state.taxes,
  isTaxEstimatedBefore: state => state.isTaxEstimatedBefore,
  isTaxUpdateAttempted: state => state.isTaxUpdateAttempted,
  isLoadingTax: state => state.isLoadingTax,
  getPlanCodeWithTaxInfo(state) {
    return state.taxes.reduce((acc, x) => {
      const planCode = x?.planCode;
      if (planCode) acc[planCode] = x;
      return acc;
    }, {});
  },
  getPlancodeMapTax(state) {
    return state.taxes.reduce((acc, x) => {
      acc[x.planCode] = x.tax.reduce((amount, y) => {
        return amount + y.taxAmount;
      }, 0);
      return acc;
    }, {});
  },
  getSelectedPlanMapList4BillingCycle(state, getter) {
    const { getSelectedPlans } = getter;

    // Dynamically get all existing billing cycles
    const billingCycles = [...new Set(getSelectedPlans.map(plan => plan.billing_cycle))];

    // Group by actual existing billing cycles
    return billingCycles.reduce((acc, billingCycle) => {
      acc[billingCycle] = plansof(getSelectedPlans).cycle(billingCycle).get();
      return acc;
    }, {});
  },
  getCycledTaxTotalAmount(state, getter) {
    const { getPlancodeMapTax, getSelectedPlanMapList4BillingCycle } = getter;

    return Object.keys(getSelectedPlanMapList4BillingCycle)
      .reduce((pre, billingCycle) => {
        const plans = getSelectedPlanMapList4BillingCycle[billingCycle];

        pre[billingCycle] = plans.reduce((acc, x) => {
          const planCode = getRealPlanCode(x);
          const amount = getPlancodeMapTax[planCode] ?? 0;
          return acc + amount;
        }, 0);

        return pre;
      }, {});
  },
  getNextCycledTaxTotalAmount(state, getter) {
    // switch feature
    if (window.$isEnabled?.("useLegacyComputedTaxInNext")) {
      return getter.getCycledTaxTotalAmount;
    }

    const { discountPlans: planWithDiscount } = getter.discountWithAmountMap;

    // if not have coupon, will use current tax total amount
    if (isEmptyVal(planWithDiscount)) {
      return getter.getCycledTaxTotalAmount;
    }

    const { getPlancodeMapTax, getPlanCodeWithTaxInfo, getSelectedPlansAmount, getSelectedPlanMapList4BillingCycle } = getter;
    const planAmountWithPlanCode = getSelectedPlansAmount.plans;

    return Object.keys(getSelectedPlanMapList4BillingCycle)
      .reduce((pre, billingCycle) => {
        const plans = getSelectedPlanMapList4BillingCycle[billingCycle] ?? [];

        pre[billingCycle] = plans.reduce((acc, x) => {
          const planCode = getRealPlanCode(x);
          const couponExpireDate = _.get(planWithDiscount, [planCode, 'expireDate']);
          // set PDT offset -7
          const nextPstCycleDateInfo = getNextPstCycleDate({ offset: -7 });
          const nextCycleKey = NEXT_CYCLE_KEY_WITH_BILLING_CYCLE_MAP[billingCycle];
          const discountedPlanAmount = getPlanCodeWithTaxInfo[planCode]?.chargeAmount ?? 0;
          const originalPlanAmount = planAmountWithPlanCode[planCode];
          const currentTaxAmount = getPlancodeMapTax[planCode] ?? 0;

          // current plan amount should more than zero & coupon will expire in next billing cycle
          if (
            discountedPlanAmount
            && originalPlanAmount
            && couponExpireDate
            && !compareDate(couponExpireDate, nextPstCycleDateInfo[nextCycleKey])
          ) {
            acc += (originalPlanAmount/discountedPlanAmount) * currentTaxAmount;
          } else {
            acc += currentTaxAmount;
          }

          return acc;
        }, 0)

        return pre;
      }, {});
  },
  getTaxTotalAmount: (state, getters) => {
    if (!getters.getSelectedPlans.length) return 0;
    if (getters.isInrCitEligible && getters.isProratedChargeAmountCalculated) return getters.getProratedChargeTaxAmount;
    return state.taxes.reduce((acc, x) => {
      const planTax = x.tax.reduce((acc, y) => {
        return acc + y.taxAmount;
      }, 0);
      return acc + planTax;
    }, 0);
  },
  getDeskphoneTaxTotalAmount: state => {
    return state.deskphoneTaxes.reduce((acc, x) => {
      return acc + x.tax;
    }, 0);
  },
  timebasedDiscountPlanEdit: (state, getters) => {
    const { getSelectedPlansWithQuantityMap, couponState, fromCampaignLink } = getters;

    if (window.$context?.is?.edit && !isEmptyVal(couponState?.timeBase) && !fromCampaignLink) {
      const selectedPlanCodeList = Object.keys(getSelectedPlansWithQuantityMap);

      return _.some(couponState.timeBase, (p) => _.includes(selectedPlanCodeList, p?.plan_code));
    }

    return false;
  },
  shippingAddressGetter: (_, getters) => {
    const shippingInfo = getters.shippingInfo || {};
    const addressInfo = getters.addressInfo;
    const country = shippingInfo.country || addressInfo.country;
    const state = shippingInfo.state || addressInfo.state;
    const city = shippingInfo.city || addressInfo.city;
    const address1 = shippingInfo.address1 || addressInfo.address1;
    const zip = shippingInfo.zip || addressInfo.zip;
    let phone = '', phoneCountry = '';
    if (shippingInfo.phone) {
      phone = shippingInfo.phone;
      phoneCountry = shippingInfo.phoneCountry;
    }
    phoneCountry && (
      phone = stringifyPhoneNumber(phoneCountry, phone)
    );
    return {
      country,
      state,
      city,
      address1,
      zip,
      phone,
    }
  },
  deskphoneTaxPayload: (_, getters) => {
    const { getSelectedPlans } = getters;
    const countryMap = getters.enCountryMap || getters.countryMap;
    const deskphones = getSelectedPlans.filter(item => item.isVendorProduct);
    return {
      currency: getEffectiveCurrency(),
      address: {
        firstName: getters.account.firstName,
        lastName: getters.account.lastName,
        companyName: getters.addressInfo.companyName,
        city: getters.shippingAddressGetter?.city,
        country: new Enums(countryMap)[getters.shippingAddressGetter?.country] || getters.shippingAddressGetter?.country,
        state: new Enums(US_short)[getters.shippingAddressGetter?.state] || getters.shippingAddressGetter?.state,
        longState: getters.shippingAddressGetter?.state,
        address1: getters.shippingAddressGetter?.address1,
        zip: getters.shippingAddressGetter?.zip,
        phone : getters.addressInfo.phone,
      },
      taxExempt: getters.isTaxExempt,
      items: deskphones.map(item => ({
        itemId: item.plan_code,
        quantity: item.cartInfo.selected_number
      }))
    };
  },
  taxPayload: (state, getters) => {
    const { soldToContact, selectedPlanListPayLoad, couponState, isTaxExempt } = getters;
    const jointsaleParams = getPartnershipParams();

    return {
      contact: [soldToContact],
      taxExempt: isTaxExempt,
      currency: getEffectiveCurrency(),
      orderData: {
        planList: selectedPlanListPayLoad,
        coupon: {
          fromCampaignLink: couponState?.fromCampaignLink ?? false,
          code: couponState?.couponCode || '',
        },
        ...(getters.referralCode ? {
          referralData: {
            referralCode: getters.referralCode,
          }
        } : {})
      },
      extension: {
        ...jointsaleParams,
        jointsaleCode: getters.partnerRefer?.jointsaleCode,
      },
      timebasedDiscountPlanEdit: getters.timebasedDiscountPlanEdit,
      ...(isFromCancelCampaign() ? { oldPlanType: window.$config("oldPlanType") } : {}),
    };
  },
  certificateId: (state) => state.certificateId,
}

const mutations = {
  setTaxes(state, data) {
    state.taxes = data;
  },
  setIsTaxEstimatedBefore(state, is){
    state.isTaxEstimatedBefore = is;
  },
  setIsTaxUpdateAttempted(state, is){
    state.isTaxUpdateAttempted = is;
  },
  setIsLoadingTax(state, is) {
    state.isLoadingTax = is;
  },
  setIsTaxEstimatedSuccess(state, is) {
    state.isTaxEstimatedSuccess = is;
  },
  setCertificateId(state, certificateId) {
    state.certificateId = certificateId;
  },
  setDeskphoneTaxes(state, deskphoneTaxes) {
    state.deskphoneTaxes = deskphoneTaxes;
  },
  setDeskphoneTaxStatus(state, deskphoneTaxStatus) {
    state.deskphoneTaxStatus = deskphoneTaxStatus;
  },
}

const actions = {
  async getDeskPhoneTax({ commit, getters }) {
    const { deskphoneTaxPayload } = getters;
    if (deskphoneTaxPayload.items.length > 0) {
      try {
        const { result: defaultEcmInfo } = await getDefaultEcmInfo(getters.shippingAddressGetter?.country, getters.shippingAddressGetter?.state);
        if (defaultEcmInfo && defaultEcmInfo.exemptStatus === ttrTaxStatusList.EXEMPT) {
          commit('setDeskphoneTaxes', []);
          commit('setDeskphoneTaxStatus', FetchStatus.SUCCESS);
          return;
        }
      } catch (e) {
        logger.error('getDefaultEcmInfo error', e);
      }

      // call desk phone api when there is no approved exemption.
      commit('setDeskphoneTaxStatus', FetchStatus.PENDING);
      try {
        const { result: deskphoneResult, status: deskphoneStatus } = await estimateDeskPhoneTax(deskphoneTaxPayload);

        if (deskphoneStatus) {
          commit('setDeskphoneTaxes', deskphoneResult.map(item => ({
            itemId: item.itemId,
            tax: item.tax * 100
          })));
        }
        commit('setDeskphoneTaxStatus', FetchStatus.SUCCESS);
      } catch (e) {
        commit('setDeskphoneTaxStatus', FetchStatus.FAILURE);
      }
    }
  },
  async estimateTax({ commit, getters, dispatch }) {
    commit('setTaxes', []);
    const { taxPayload, paymentMethodUpdated,  } = getters;
    try {
      await dispatch('getDeskPhoneTax');
      const { result, status } = await estimateTax(taxPayload);
      commit('setIsTaxEstimatedBefore', true);
      if (!status) return;
      commit('setIsTaxEstimatedSuccess', true);
      const { taxEstimate = [], cvvReAuth = getDefaultCvvReAuthInfo()} = result;
      commit('setTaxes', taxEstimate);
      if (!paymentMethodUpdated) {
        commit('setCvvReAuthInfo', cvvReAuth);
      }
    } catch (e) {
      commit('setIsTaxEstimatedSuccess', false);
    }
  }
}

export default {
  state,
  getters,
  mutations,
  actions,
}
